import type {
  AllowedOrganisationProduct,
  OAuthClientResponseData,
  UserResponseData,
} from "common-services/types/types";

import {
  allowedProducts,
  productDescriptions,
} from "common-services/constants";
import {
  OAuthClientResponse,
  UserResponse,
} from "generated/api/organisations/models";

const formatProductOptionLabel = (
  product: AllowedOrganisationProduct,
): string =>
  `${product === "ads" ? "ADS" : product} - ${productDescriptions[product]}`;

export const productSelectOptions = allowedProducts.map(product => ({
  value: product,
  label: formatProductOptionLabel(product),
}));

interface PasswordFlowLike {
  emailHint: string;
  error: string;
  info: string;
  navigateTo: (
    view: { kind: "credentials" } | { kind: "forgotPassword" },
    options?: { info?: string },
  ) => void;
  submitCredentials: (values: { email: string; password: string }) => void;
  submitForgotConfirm: (values: { code: string; newPassword: string }) => void;
  submitForgotPassword: (values: { email: string }) => void;
  submitNewPassword: (values: { newPassword: string }) => void;
  submitting: boolean;
  view: { kind: string; username?: string };
}

export const toPasswordFlowFormsProps = (
  flow: PasswordFlowLike,
  onCancel: () => void,
) => ({
  viewKind: flow.view.kind,
  forgotConfirmUsername:
    flow.view.kind === "forgotConfirm" ? flow.view.username : undefined,
  submitting: flow.submitting,
  error: flow.error,
  info: flow.info,
  emailHint: flow.emailHint,
  onCancel,
  onBackToSignIn: () => flow.navigateTo({ kind: "credentials" }),
  onForgotPassword: () => flow.navigateTo({ kind: "forgotPassword" }),
  onResendCode: () => flow.navigateTo({ kind: "forgotPassword" }),
  submitCredentials: flow.submitCredentials,
  submitNewPassword: flow.submitNewPassword,
  submitForgotPassword: flow.submitForgotPassword,
  submitForgotConfirm: flow.submitForgotConfirm,
});

export const fromUserResponse = (data: UserResponse): UserResponseData => ({
  userId: data.user_id,
  email: data.email,
  givenName: data.given_name ?? "",
  familyName: data.family_name ?? "",
  organisationId: data.organisation_id,
  organisationName: data.organisation_name,
  groupId: data.group_id ?? "",
  threddRoles: data.thredd_roles ?? [],
  temporaryPassword: data.temporary_password ?? "",
  invitationSent: data.invitation_sent,
  status: data.status,
  restrictedCodes: data.restricted_codes ?? [],
  createdDate: data.created_date ?? null,
  lastModifiedDate: data.last_modified_date ?? null,
});

export const fromOAuthClientResponse = (
  data: OAuthClientResponse,
): OAuthClientResponseData => ({
  clientId: data.client_id,
  clientSecret: data.client_secret,
  organisationId: data.organisation_id,
  applicationId: data.application_id,
  pmId: data.pm_id,
  pmCode: data.pm_code,
  issuerId: data.issuer_id,
  internalId: data.internal_id,
  name: data.name,
  description: data.description,
  allowedGroupIds: data.allowed_group_ids,
  scopes: data.scopes,
  isActive: data.is_active,
  lastSecretRotation: data.last_secret_rotation,
  ipWhitelist: data.ip_whitelist,
  createdAt: data.created_at,
  createdBy: data.created_by,
  updatedAt: data.updated_at,
  usageNotes: data.usage_notes,
});
